/**
 * Admin Users Page - Microsoft 365 Style
 * Create and manage admin user accounts for employees
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Avatar,
  Badge,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
} from '@fluentui/react-components';
import {
  PersonRegular,
  MoreHorizontalRegular,
  EditRegular,
  PersonProhibitedRegular,
  PersonAvailableRegular,
} from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../components/layout';
import { TablePagination } from '../../components/common';
import { usePagination } from '../../hooks/usePagination';
import { ApiClient, newToast, fullDate } from '../../configurations/index.config';
import { getErrorMessage } from '../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  passwordField: {
    position: 'relative',
  },
  passwordToggle: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionsCell: {
    width: '80px',
    textAlign: 'center',
  },
  menuButton: {
    minWidth: 'auto',
  },
});

interface AdminUser {
  id: string;
  username: string;
  employee_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  created_at?: string;
  status?: string;
}

export const AdminUsersFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [actionType, setActionType] = useState<'activate' | 'deactivate' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Pagination
  const { currentPage, itemsPerPage, paginatedData, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination({
      data: users,
      initialPage: 1,
      initialItemsPerPage: 10,
    });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-user-data');
      if (response.data.status === 'success') {
        setUsers(response.data.data || []);
      } else {
        newToast('error', response.data?.message || 'Failed to fetch users');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleAddUser = () => {
    navigate('/account/create-admin-user');
  };

  const handleEdit = (user: AdminUser) => {
    navigate('/account/create-admin-user', { state: { editUser: user } });
  };

  const handleOpenDialog = (user: AdminUser, action: 'activate' | 'deactivate') => {
    setSelectedUser(user);
    setActionType(action);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setActionType(null);
  };

  const handleConfirmAction = async () => {
    if (!selectedUser || !actionType) {
      return;
    }

    setSubmitting(true);
    try {
      const newStatus = actionType === 'activate' ? 'active' : 'inactive';
      const response = await ApiClient.post('/add-new-user', {
        id: selectedUser.id,
        employee_id: selectedUser.employee_id,
        username: selectedUser.username,
        password: '',
        confirmPassword: '',
        status: newStatus,
      });

      if (response.data.status === 'success') {
        newToast(
          'success',
          `User ${actionType === 'activate' ? 'activated' : 'deactivated'} successfully`
        );
        fetchUsers();
        handleCloseDialog();
      } else {
        newToast('error', response.data?.message || 'Failed to update user status');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setSubmitting(false);
  };

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Admin Users</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${users.length} admin accounts`}
        </Text>
      </div>

      {/* Command Bar */}
      <TableCommandBar onAdd={handleAddUser} addLabel="Add Admin User" />

      {/* Users Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading admin users...</Text>
          </div>
        ) : users.length === 0 ? (
          <div className={styles.emptyState}>
            <PersonRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No admin users found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              Click "Add Admin User" to create an admin account for an employee
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>User</th>
                <th className={styles.th}>Username</th>
                <th className={styles.th}>Email</th>
                <th className={styles.th}>Created</th>
                <th className={styles.th}>Status</th>
                <th className={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(user => (
                <tr key={user.id} className={styles.tr}>
                  <td className={styles.td}>
                    <div className={styles.userCell}>
                      <Avatar
                        name={`${user.first_name} ${user.last_name}`}
                        color="brand"
                        size={32}
                      />
                      <div>
                        <Text weight="semibold" style={{ display: 'block' }}>
                          {user.first_name} {user.last_name}
                        </Text>
                      </div>
                    </div>
                  </td>
                  <td className={styles.td}>
                    <Badge appearance="outline" color="brand">
                      {user.username}
                    </Badge>
                  </td>
                  <td className={styles.td}>{user.email || '—'}</td>
                  <td className={styles.td}>{user.created_at ? fullDate(user.created_at) : '—'}</td>
                  <td className={styles.td}>
                    <Badge
                      appearance="tint"
                      color={user.status === 'active' ? 'success' : 'subtle'}
                    >
                      {user.status === 'active' ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className={`${styles.td} ${styles.actionsCell}`}>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button
                          appearance="subtle"
                          icon={<MoreHorizontalRegular />}
                          className={styles.menuButton}
                          size="small"
                        />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem icon={<EditRegular />} onClick={() => handleEdit(user)}>
                            Edit
                          </MenuItem>
                          {user.status === 'active' ? (
                            <MenuItem
                              icon={<PersonProhibitedRegular />}
                              onClick={() => handleOpenDialog(user, 'deactivate')}
                            >
                              Deactivate
                            </MenuItem>
                          ) : (
                            <MenuItem
                              icon={<PersonAvailableRegular />}
                              onClick={() => handleOpenDialog(user, 'activate')}
                            >
                              Activate
                            </MenuItem>
                          )}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && users.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(_, data) => !submitting && setDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {actionType === 'activate' ? 'Activate User Account?' : 'Deactivate User Account?'}
            </DialogTitle>
            <DialogContent>
              {selectedUser && (
                <div style={{ marginTop: '16px' }}>
                  <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                    Username: {selectedUser.username}
                  </Text>
                  <Text style={{ display: 'block', marginBottom: '16px' }}>
                    Name: {selectedUser.first_name} {selectedUser.last_name}
                  </Text>
                  {actionType === 'deactivate' ? (
                    <Text style={{ color: '#d13438' }}>
                      This user will no longer be able to login. All transaction history will be
                      preserved for audit purposes.
                    </Text>
                  ) : (
                    <Text style={{ color: '#107c10' }}>
                      This user will be able to login and access the system again.
                    </Text>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleCloseDialog} disabled={submitting}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleConfirmAction} disabled={submitting}>
                {submitting
                  ? actionType === 'activate'
                    ? 'Activating...'
                    : 'Deactivating...'
                  : actionType === 'activate'
                    ? 'Activate Account'
                    : 'Deactivate Account'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default AdminUsersFluentUI;
